import React, { useState, useEffect } from 'react';
import { galleryData } from '../../data/gallery';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [layout, setLayout] = useState("masonry"); // masonry or grid
  const [isLoading, setIsLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [lightboxLoading, setLightboxLoading] = useState(false);
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Extract all unique categories and tags
  const allCategories = ["All", ...Array.from(new Set(galleryData.map(item => item.category)))];
  const allTags = Array.from(new Set(galleryData.flatMap(item => item.tags)));
  
  // Filter gallery items based on category and search term
  const filteredGallery = galleryData.filter(item => {
    const matchesCategory = filter === "All" || item.category === filter;
    const matchesSearch = searchTerm === "" || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
    return matchesCategory && matchesSearch;
  });
  
  // Function to handle image load error
  const handleImageError = (id) => {
    console.error(`Failed to load image for item with ID: ${id}`);
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };
  
  // Function to open the full image view
  const openLightbox = (imageId) => {
    setLightboxLoading(true);
    setSelectedImage(imageId);
    document.body.style.overflow = "hidden"; // Prevent background scrolling
    
    // Simulate image loading for smooth transition
    setTimeout(() => {
      setLightboxLoading(false);
    }, 300);
  };
  
  // Function to close the full image view
  const closeLightbox = () => {
    setSelectedImage(null);
    setLightboxLoading(false);
    document.body.style.overflow = "auto"; // Restore scrolling
  };
  
  // Get the selected image data
  const selectedImageData = selectedImage !== null 
    ? galleryData.find(item => item.id === selectedImage) 
    : null;
  
  // Navigate to the next or previous image in lightbox
  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    setLightboxLoading(true);
    const currentIndex = filteredGallery.findIndex(item => item.id === selectedImage);
    const nextIndex = (currentIndex + direction + filteredGallery.length) % filteredGallery.length;
    
    setSelectedImage(filteredGallery[nextIndex].id);
    
    setTimeout(() => {
      setLightboxLoading(false);
    }, 200);
  };
  
  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") navigateImage(1);
      if (e.key === "ArrowLeft") navigateImage(-1);
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, filteredGallery]);
  
  return (
    <div className="py-16 relative">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
      </div>

      
      {/* Filter controls */}
      <div className="max-w-7xl mx-auto mb-8 px-4 sm:px-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex overflow-x-auto pb-2 hide-scrollbar md:pb-0 md:flex-wrap gap-2">
            {allCategories.map(category => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
                  filter === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search photos..."
              className="block w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg py-2 pl-10 pr-3 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setLayout('masonry')}
              className={`p-2 rounded-lg ${
                layout === 'masonry' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="Masonry layout"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded-lg ${
                layout === 'grid' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="Grid layout"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-center py-20">
          <div className="flex flex-col items-center">
            <div className="relative w-12 h-12">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading gallery...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Gallery grid */}
          {filteredGallery.length > 0 ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {layout === 'masonry' ? (
                <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
                  {filteredGallery.map(item => {
                    // Determine height based on aspect ratio
                    const getImageHeight = (aspectRatio) => {
                      switch(aspectRatio) {
                        case '4/3': return 'h-64 md:h-80 lg:h-96'; // Landscape
                        case '3/2': return 'h-56 md:h-72 lg:h-80'; // Wide landscape
                        case '2/1': return 'h-48 md:h-64 lg:h-72'; // Very wide landscape
                        case '3/4': return 'h-80 md:h-96 lg:h-[28rem]'; // Portrait
                        case '2/3': return 'h-72 md:h-80 lg:h-96'; // Tall portrait
                        case '1/1': return 'h-64 md:h-80 lg:h-80'; // Square
                        default: return 'h-64 md:h-80 lg:h-96';
                      }
                    };
                    
                    return (
                      <div 
                        key={item.id} 
                        className="relative overflow-hidden break-inside-avoid rounded-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/20 mb-6 group"
                        onClick={() => openLightbox(item.id)}
                      >
                        <div className={`bg-gray-200 dark:bg-gray-700 overflow-hidden ${getImageHeight(item.aspectRatio)}`}>
                          {imageErrors[item.id] ? (
                            <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-2xl font-bold">
                              {item.title.charAt(0)}
                            </div>
                          ) : (
                            <img 
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                              onError={() => handleImageError(item.id)}
                            />
                          )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                          <h3 className="text-white text-xl font-semibold mb-2">{item.title}</h3>
                          <p className="text-gray-200 text-sm mb-3 leading-relaxed">{item.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {item.tags.map(tag => (
                              <span key={tag} className="text-xs bg-white/20 text-gray-100 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredGallery.map(item => {
                    // For grid view, make landscape photos span more columns when possible
                    const isLandscape = ['4/3', '3/2', '2/1'].includes(item.aspectRatio);
                    const isWide = ['3/2', '2/1'].includes(item.aspectRatio);
                    
                    return (
                      <div 
                        key={item.id} 
                        className={`group relative overflow-hidden rounded-xl cursor-pointer ${
                          isWide ? 'md:col-span-2 xl:col-span-2' : ''
                        }`}
                        onClick={() => openLightbox(item.id)}
                      >
                        <div className={`overflow-hidden ${
                          isLandscape ? 'aspect-[4/3]' : 
                          item.aspectRatio === '3/4' ? 'aspect-[3/4]' : 
                          item.aspectRatio === '2/3' ? 'aspect-[2/3]' : 
                          'aspect-square'
                        }`}>
                          {imageErrors[item.id] ? (
                            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-3xl font-bold">
                              {item.title.charAt(0)}
                            </div>
                          ) : (
                            <img 
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                              onError={() => handleImageError(item.id)}
                            />
                          )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                          <h3 className="text-white text-xl font-semibold mb-2">{item.title}</h3>
                          <p className="text-gray-200 text-sm mb-3 leading-relaxed">{item.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {item.tags.map(tag => (
                              <span key={tag} className="text-xs bg-white/20 text-gray-100 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>       
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center py-16 border border-gray-200 dark:border-gray-700/50 rounded-lg">
              <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl text-gray-600 dark:text-gray-400">No photos match your search</h3>
              <button 
                onClick={() => {setSearchTerm(""); setFilter("All");}}
                className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
              >
                Clear filters
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Simplified Lightbox without technical details */}
      {selectedImage !== null && selectedImageData && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center animate-fadeIn">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
          
          {/* Close button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-20 p-3 rounded-full bg-black/50 backdrop-blur-md text-white hover:text-gray-300 hover:bg-black/70 transition-all duration-300 hover:scale-110 group"
            aria-label="Close lightbox"
          >
            <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Navigation buttons */}
          <button 
            onClick={() => navigateImage(-1)}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 p-4 rounded-full bg-black/50 backdrop-blur-md text-white hover:text-gray-300 hover:bg-black/70 transition-all duration-300 hover:scale-110 group"
            aria-label="Previous image"
          >
            <svg className="w-8 h-8 group-hover:-translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button 
            onClick={() => navigateImage(1)}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 p-4 rounded-full bg-black/50 backdrop-blur-md text-white hover:text-gray-300 hover:bg-black/70 transition-all duration-300 hover:scale-110 group"
            aria-label="Next image"
          >
            <svg className="w-8 h-8 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Main content container */}
          <div className="w-full h-full flex flex-col lg:flex-row max-w-7xl mx-auto p-6 gap-8">
            
            {/* Image container */}
            <div className="flex-1 flex items-center justify-center relative">
              {lightboxLoading ? (
                <div className="flex items-center justify-center h-96 w-96">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-white/20 rounded-full animate-spin"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-white rounded-full animate-spin"></div>
                  </div>
                </div>
              ) : (
                <div className={`relative max-w-full max-h-full ${lightboxLoading ? 'animate-pulse' : 'animate-scaleIn'}`}>
                  {imageErrors[selectedImageData.id] ? (
                    <div className="w-96 h-96 flex items-center justify-center text-gray-400 text-6xl font-bold bg-gray-800 rounded-2xl">
                      {selectedImageData.title.charAt(0)}
                    </div>
                  ) : (
                    <img 
                      src={selectedImageData.imageUrl}
                      alt={selectedImageData.title}
                      className="max-h-[85vh] max-w-full object-contain rounded-2xl shadow-2xl shadow-black/50"
                      onError={() => handleImageError(selectedImageData.id)}
                    />
                  )}
                </div>
              )}
            </div>
            
            {/* Simplified info panel */}
            <div className="lg:w-96 lg:min-h-full flex flex-col">
              <div className="bg-black/40 backdrop-blur-md rounded-2xl p-8 border border-white/10 animate-slideInRight">
                
                {/* Title and description */}
                <div className="mb-8">
                  <h3 className="text-3xl font-bold text-white mb-4 leading-tight">{selectedImageData.title}</h3>
                  <p className="text-gray-300 text-lg leading-relaxed">{selectedImageData.description}</p>
                </div>
                
                {/* Category */}
                <div className="mb-8">
                  <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10">
                    <p className="text-gray-400 text-sm font-medium mb-1">Category</p>
                    <p className="text-white font-semibold">{selectedImageData.category}</p>
                  </div>
                </div>
                
                {/* Tags */}
                <div>
                  <p className="text-gray-400 text-sm font-medium mb-3">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedImageData.tags.map((tag, index) => (
                      <span 
                        key={tag} 
                        className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white rounded-full backdrop-blur-sm border border-white/20 text-sm animate-fadeIn"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom animations */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.9);
          }
          to { 
            opacity: 1; 
            transform: scale(1);
          }
        }
        
        @keyframes slideInRight {
          from { 
            opacity: 0; 
            transform: translateX(30px);
          }
          to { 
            opacity: 1; 
            transform: translateX(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.6s ease-out forwards;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        `}
      </style>
    </div>
  );
};

export default GallerySection;